import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Form, Button, Card } from 'react-bootstrap';
import { ArrowUpRight } from 'react-bootstrap-icons';
import ReCAPTCHA from 'react-google-recaptcha';
import AuthCardLeft from './AuthCardLeft';
import MobileDesktopOnlyModal from './MobileDesktopOnlyModal';
import api from '../../services/api';
import './Auth.css';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [message, setMessage] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setError('');
    setEmailError('');
    setMessage('');

    // Custom validation
    if (!email) {
      setEmailError('Email is required');
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    // Validate reCAPTCHA
    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA verification');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post<{ success: boolean; message: string }>('/api/password-reset/request', {
        email,
        recaptchaToken
      });
      
      if (response.success) {
        setMessage(response.message || 'Check your inbox for password reset instructions');
        setResetSent(true);
        setEmail('');
        setRecaptchaToken(null);
        recaptchaRef.current?.reset();
      }
    } catch (error: any) {
      if (error.response?.status === 400) {
        setEmailError(error.response.data.error || 'Invalid email address');
      } else if (error.response?.status === 429) {
        setError('Too many requests. Please try again later');
      } else {
        setError('Failed to send reset email. Please try again');
        console.error('Password reset error:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <MobileDesktopOnlyModal />
      <Card className="auth-card">
        <Card.Body>
          <AuthCardLeft />
          <div className="auth-card-right">
            <div style={{ width: '100%', maxWidth: '400px' }}>
              <h1 className="text-center">Reset your password</h1>
              {!resetSent && <h2 className="text-muted mb-4">Enter your user account's verified email address, and we'll send you a password reset link.</h2>}
              {error && <div className="text-danger text-center mb-3">{error}</div>}
              {message && <h2>{message}</h2>}
              {!resetSent &&
                <Form onSubmit={handleSubmit} noValidate>
                  <Form.Group id="email" className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailError('');
                      }}
                      disabled={loading || !!message}
                      placeholder="name@example.com"
                      isInvalid={!!emailError}
                    />
                    {emailError && <Form.Text className="text-danger">{emailError}</Form.Text>}
                  </Form.Group>

                  {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                    <div className="mb-3" style={{ width: '100%' }}>
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                        onChange={(token) => setRecaptchaToken(token)}
                        onExpired={() => setRecaptchaToken(null)}
                        style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                        size="normal"
                      />
                    </div>
                  )}

                  <Button 
                    disabled={loading || !!message} 
                    className="w-100" 
                    type="submit"
                  >
                    {loading ? 'SENDING...' : 'SEND PASSWORD RESET EMAIL'}
                  </Button>
                </Form>
              }
              {resetSent &&
                <div className="w-100 text-center mt-4">
                  Go back to &nbsp;<Link to="/signin">SIGN IN <ArrowUpRight className="me-2" /></Link>
                </div>
              }
              {!resetSent &&
                <div className="w-100 text-center mt-2">
                  Don't have an account? &nbsp;<Link to="/register">REGISTER <ArrowUpRight className="me-2" /></Link>
                </div>
              }
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ForgotPassword;